import { getSymbolFont, isLargeOperator } from "@corca/core";
import { computed, makeObservable } from "mobx";

import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { DecoratorNodeModel } from "@/models/nodes/DecoratorNodeModel";
import { LabelNodeModel } from "@/models/nodes/LabelNodeModel";
import { isNodeHighlighted } from "@/models/nodes/utils";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel, NodeId } from "@/models/nodes/types";
import type { VisitableNode, NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";
import type {
  SymbolFontContext,
  SymbolFont,
  SymbolNodeProps,
  SymbolNodeConstructorProps,
  LabelNodePosition,
} from "@corca/data";

const labelsSortOrder: LabelNodePosition[] = [
  "right-bottom",
  "right-top",
  "left-bottom",
  "left-top",
];

export class SymbolNodeModel extends BaseNodeModel<SymbolNodeProps> implements VisitableNode {
  static type = "symbol" as const;

  type = SymbolNodeModel.type;

  protected fillRequiredProps(props: SymbolNodeConstructorProps): SymbolNodeProps {
    this.reportMissingProps(props, ["value"]);
    return { ...props, value: props.value ?? "" };
  }

  static create(
    doc: DocumentModel,
    value: string,
    props: Omit<SymbolNodeConstructorProps, "value"> = {},
    nodes: NodeModel[] = [],
    id?: NodeId,
  ) {
    return new SymbolNodeModel(doc, id, { ...props, value }, nodes);
  }

  constructor(...args: ConstructorParameters<typeof BaseNodeModel<SymbolNodeConstructorProps>>) {
    super(...args);

    makeObservable(this, {
      labelNodes: computed,
      decorators: computed,
      nonEmptyAttachments: true,
      isHighlighted: computed,
    });
  }

  get value(): string {
    return this.props.value;
  }

  set value(value: string) {
    this.updateProps({ value });
  }

  get labeledValue(): string {
    const subscriptLabels = this.labelNodes
      .map((l) => l.position === "right-bottom" && l.textContent.trim())
      .filter(Boolean);

    const superscriptLabels = this.labelNodes
      .map((l) => l.position === "right-top" && l.textContent.trim())
      .filter(Boolean);

    let result = this.value;
    if (subscriptLabels.length > 0) {
      result = `${result}_${subscriptLabels.join("")}`;
    }
    if (superscriptLabels.length > 0) {
      result = `${result}__sup_${superscriptLabels.join("")}`;
    }
    return result;
  }

  getFont(context: SymbolFontContext): SymbolFont {
    const { key, value } = this.props;
    return getSymbolFont({ key, value, props: this.props }, context);
  }

  get isLargeOperator(): boolean {
    const { key, value } = this.props;
    return isLargeOperator({ key, value, props: this.props });
  }

  get labelNodes() {
    return this.children.filter((n) => n.is(LabelNodeModel));
  }

  get sortedLabelNodes() {
    return [...this.labelNodes].sort(
      (a, b) => labelsSortOrder.indexOf(a.position) - labelsSortOrder.indexOf(b.position),
    );
  }

  get decorators() {
    return this.children.filter((n) => n.is(DecoratorNodeModel));
  }

  hasLabels(side?: "left" | "right") {
    return this.labelNodes.some((labelNode) => {
      if (labelNode.isEmpty) return false;
      if (side === "left")
        return labelNode.position === "left-top" || labelNode.position === "left-bottom";
      if (side === "right")
        return labelNode.position === "right-top" || labelNode.position === "right-bottom";
      return true;
    });
  }

  get isHighlighted(): boolean {
    return isNodeHighlighted(this.doc, this.parent);
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitSymbolNode(this);
  }

  get nonEmptyAttachments() {
    const labelNodes = this.attachments.filter((n) => n.is(LabelNodeModel));
    return labelNodes.filter((att) => !!att.textContent.trim());
  }
}
